import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SEO from '@/components/SEO';
import { Reveal } from '@/lib/useReveal';
import { BLOGS } from '@/lib/blogs';
import { CONTACT_INFO, SEO_DEFAULTS } from '@/lib/content';
import { ArrowLeft, ArrowRight, Phone, Calendar, Tag } from 'lucide-react';

export default function BlogDetailPage() {
  const { slug } = useParams();
  const post = useMemo(() => BLOGS.find((b) => b.slug === slug), [slug]);

  const related = useMemo(() => {
    if (!post) return [];
    return BLOGS.filter((b) => b.slug !== post.slug && b.category === post.category).slice(0, 3);
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const heroImg = post.hero || post.img;
  const path = `/blog/${post.slug}`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description || post.excerpt,
    image: heroImg,
    datePublished: post.date,
    author: { '@type': 'Organization', name: SEO_DEFAULTS.siteName },
    publisher: {
      '@type': 'Organization',
      name: SEO_DEFAULTS.siteName,
      logo: { '@type': 'ImageObject', url: `${SEO_DEFAULTS.domain}/og-default.jpg` },
    },
    mainEntityOfPage: `${SEO_DEFAULTS.domain}${path}`,
  };

  return (
    <main data-testid="blog-detail-page" className="bg-white">
      <SEO
        title={post.title}
        description={post.description || post.excerpt}
        path={path}
        image={heroImg}
        type="article"
        schema={schema}
      />

      {/* HERO */}
      <section className="pt-10 lg:pt-16 pb-8 lg:pb-12 bg-radial-blue relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none" />
        <div className="max-w-[920px] mx-auto px-5 lg:px-8 relative">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-bp-blue hover:underline" data-testid="back-to-blog">
            <ArrowLeft size={14} /> Back to Blog
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-[12px] font-semibold uppercase tracking-wider">
            <span className="inline-flex items-center gap-1.5 bg-bp-blue-50 text-bp-blue rounded-full px-3 py-1">
              <Tag size={12} /> {post.category}
            </span>
            <span className="inline-flex items-center gap-1.5 text-bp-slate-soft normal-case tracking-normal">
              <Calendar size={13} /> {post.date}
            </span>
          </div>
          <Reveal as="h1" className="h-display mt-5 text-[32px] sm:text-[44px] lg:text-[56px] leading-[1.05]">
            {post.title}
          </Reveal>
          {post.excerpt && (
            <Reveal as="p" delay={80} className="mt-5 text-[17px] text-bp-slate leading-relaxed max-w-[760px]">
              {post.excerpt}
            </Reveal>
          )}
        </div>
      </section>

      {/* HERO IMAGE */}
      {heroImg && (
        <section className="pb-10">
          <div className="max-w-[1100px] mx-auto px-5 lg:px-8">
            <div className="overflow-hidden rounded-3xl border border-bp-slate-100 shadow-xl">
              <img
                src={heroImg}
                alt={post.title}
                className="w-full h-auto max-h-[560px] object-cover"
                loading="eager"
                data-testid="blog-hero-image"
              />
            </div>
          </div>
        </section>
      )}

      {/* BODY */}
      <article className="pb-16 lg:pb-24">
        <div className="max-w-[760px] mx-auto px-5 lg:px-8">
          <div
            className="blog-prose"
            data-testid="blog-content"
            dangerouslySetInnerHTML={{ __html: post.contentHtml || `<p>${post.excerpt}</p>` }}
          />

          <div className="mt-12 pt-8 border-t border-bp-slate-100 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary" data-testid="blog-cta-contact">
              <Phone size={15} /> Book A Strategy Call
            </Link>
            <Link to="/blog" className="btn-secondary" data-testid="blog-cta-more">
              More Articles <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </article>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="py-16 lg:py-20 bg-bp-slate-50 border-t border-bp-slate-100">
          <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
            <h2 className="h-display text-[28px] sm:text-[36px] mb-8">More from <span className="grad-text">{post.category}</span></h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/blog/${a.slug}`}
                  className="card-soft overflow-hidden group flex flex-col"
                  data-testid={`related-blog-${a.slug}`}
                >
                  <div className="overflow-hidden aspect-[4/3] bg-bp-slate-50">
                    <img src={a.img} alt={a.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]" loading="lazy" />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-bp-blue">{a.category} · <span className="text-bp-slate-soft font-medium normal-case tracking-normal">{a.date}</span></div>
                    <h3 className="font-display font-bold text-bp-ink text-[18px] leading-snug mt-3 group-hover:text-bp-blue transition-colors">{a.title}</h3>
                    <p className="mt-2.5 text-[13.5px] leading-relaxed text-bp-slate flex-1 line-clamp-3">{a.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 mt-4 text-[13px] font-semibold text-bp-blue">Read More <ArrowRight size={13} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 text-center">
          <h2 className="h-display text-[34px] sm:text-[48px]">Ready to Be Our Next <span className="grad-text">Success Story?</span></h2>
          <p className="mt-5 text-[17px] text-bp-slate max-w-xl mx-auto">Let us build a website that generates leads and grows your business.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-primary"><Phone size={15} /> Book A Strategy Call</Link>
            <Link to="/portfolio" className="btn-secondary">View Portfolio <ArrowRight size={15} /></Link>
          </div>
          <div className="mt-6 text-[13px] text-bp-slate-soft">
            Or call us at{' '}
            <a href={`tel:${CONTACT_INFO.phoneTel}`} className="font-semibold text-bp-slate hover:text-bp-blue">
              {CONTACT_INFO.phone}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
